import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Shield, 
  Save, 
  Loader2, 
  Globe,
  RefreshCw,
  CheckCircle,
  XCircle,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProxySettings() {
  const [settings, setSettings] = useState({
    proxy_string: "",
    enabled: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/proxy`, {
        headers: getAuthHeaders()
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching proxy settings:", error);
      toast.error("Ошибка загрузки настроек");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/settings/proxy`, settings, {
        headers: getAuthHeaders()
      });
      toast.success("Настройки прокси сохранены!");
      setTestResult(null);
    } catch (error) {
      console.error("Error saving proxy settings:", error);
      const message = error.response?.data?.detail || "Ошибка сохранения настроек";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setTestResult(null);
  };

  const testProxy = async () => {
    if (!settings.proxy_string) {
      toast.error("Введите прокси для тестирования");
      return;
    }
    
    setIsTesting(true);
    setTestResult(null);
    
    try {
      // Save first, then test by making a simple request
      await axios.post(`${API}/settings/proxy`, settings, {
        headers: getAuthHeaders()
      });
      
      // Test by checking IP
      const response = await axios.get(`${API}/test/proxy`, {
        headers: getAuthHeaders(),
        timeout: 30000
      });
      
      if (response.data.success) {
        setTestResult({
          success: true,
          ip: response.data.ip,
          message: "Прокси работает!"
        });
        toast.success("Прокси работает!");
      } else {
        setTestResult({
          success: false,
          message: response.data.error || "Ошибка подключения"
        });
        toast.error(response.data.error || "Ошибка подключения к прокси");
      }
    } catch (error) {
      console.error("Proxy test error:", error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || "Ошибка тестирования прокси"
      });
      toast.error("Ошибка тестирования прокси");
    } finally {
      setIsTesting(false);
    }
  };

  // Parse proxy for preview
  const parseProxyPreview = (proxyString) => {
    if (!proxyString) return null;
    const parts = proxyString.split(':');
    if (parts.length === 4) {
      return {
        ip: parts[0],
        port: parts[1],
        user: parts[2],
        pass: '****'
      };
    } else if (parts.length === 2) {
      return {
        ip: parts[0],
        port: parts[1],
        user: null,
        pass: null
      };
    }
    return null;
  };

  const proxyPreview = parseProxyPreview(settings.proxy_string);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="proxy-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-5 h-5 text-white" />
            </div>
            Настройки Прокси
          </h1>
          <p className="text-slate-500 mt-1">
            Прокси для OZON парсера (обход блокировок)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Обновить
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Сохранить
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Proxy Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5 text-purple-500" />
              Конфигурация прокси
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="font-medium">Прокси включён</span>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => handleChange("enabled", checked)}
              />
            </div>
            
            <div>
              <Label>Прокси сервер</Label>
              <Input
                value={settings.proxy_string}
                onChange={(e) => handleChange("proxy_string", e.target.value)}
                placeholder="ip:port:user:pass"
                className="font-mono"
              />
              <p className="text-xs text-slate-400 mt-1">
                Формат: <code className="bg-slate-100 px-1 rounded">ip:port:user:pass</code> или{" "}
                <code className="bg-slate-100 px-1 rounded">http://user:pass@ip:port</code>
              </p>
            </div>

            {proxyPreview && (
              <div className="p-3 bg-slate-50 rounded-lg space-y-1 text-sm">
                <div className="font-medium text-slate-700">Распознано:</div>
                <div className="grid grid-cols-2 gap-2 text-slate-600">
                  <div>IP: <span className="font-mono">{proxyPreview.ip}</span></div>
                  <div>Порт: <span className="font-mono">{proxyPreview.port}</span></div>
                  {proxyPreview.user && (
                    <>
                      <div>Логин: <span className="font-mono">{proxyPreview.user}</span></div>
                      <div>Пароль: <span className="font-mono">{proxyPreview.pass}</span></div>
                    </>
                  )}
                </div>
              </div>
            )}

            <Button 
              variant="outline" 
              onClick={testProxy}
              disabled={isTesting || !settings.proxy_string}
              className="w-full"
            >
              {isTesting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Shield className="w-4 h-4 mr-2" />
              )}
              Тестировать прокси
            </Button>

            {testResult && (
              <div className={`p-3 rounded-lg flex items-center gap-2 ${
                testResult.success 
                  ? 'bg-green-50 text-green-700' 
                  : 'bg-red-50 text-red-700'
              }`}>
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                <div>
                  <div className="font-medium">{testResult.message}</div>
                  {testResult.ip && (
                    <div className="text-sm opacity-80">IP: {testResult.ip}</div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Info className="w-5 h-5 text-blue-500" />
              Информация
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg text-blue-800">
              <div className="font-medium mb-2">Зачем нужен прокси?</div>
              <p className="text-sm">
                OZON может блокировать IP адреса серверов/VPS. Прокси позволяет 
                обойти эти блокировки и успешно парсить данные.
              </p>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg text-amber-800">
              <div className="font-medium mb-2">Рекомендации:</div>
              <ul className="text-sm space-y-1 list-disc list-inside">
                <li>Используйте резидентные прокси (не датацентровые)</li>
                <li>Прокси должен быть из России</li>
                <li>Рекомендуется HTTPS или SOCKS5</li>
                <li>При частых блокировках - меняйте прокси</li>
              </ul>
            </div>

            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="font-medium mb-2 text-slate-700">Поддерживаемые форматы:</div>
              <ul className="text-sm space-y-1 text-slate-600 font-mono">
                <li>ip:port:user:pass</li>
                <li>http://user:pass@ip:port</li>
                <li>socks5://user:pass@ip:port</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
